import React, { useState, useEffect, useRef, useCallback } from "react";
import * as faceapi from "face-api.js";
import { attendanceAPI, usersAPI, formatError } from "../utils/api";
import { useConfirm } from "../hooks/useConfirm";
import { fmtTime } from "../utils/dateUtils";
import {
  Camera, Users, Clock, BarChart3, Settings, X, Plus, Edit, Trash2,
  CheckCircle, XCircle, AlertTriangle, Loader, Shield, RefreshCw,
  UserCheck, UserX, LogIn, LogOut, ChevronDown
} from "lucide-react";

const MODEL_URL = "/models";

// ─── Face Recognition Hook ───────────────────────────────────────────────────
function useFaceModels() {
  const { confirm, ConfirmModal } = useConfirm();
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    // If already loaded in face-api.js memory, just mark as ready
    if (faceapi.nets.tinyFaceDetector.isLoaded &&
        faceapi.nets.faceLandmark68TinyNet.isLoaded &&
        faceapi.nets.faceRecognitionNet.isLoaded) {
      setLoaded(true);
      return;
    }
    if (loading) return;
    setLoading(true);
    try {
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setLoaded(true);
    } catch (e) {
      setError("Failed to load face recognition models: " + e.message);
    }
    setLoading(false);
  }, [loading]);

  return { loaded, loading, error, load };
}

// ─── Camera component ────────────────────────────────────────────────────────
function CameraView({ onFrame, onCapture, mirrored = true }) {
  const videoRef = useRef();
  const canvasRef = useRef();
  const [active, setActive] = useState(false);
  const [err, setErr] = useState("");

  const start = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: "user" } });
      videoRef.current.srcObject = stream;
      videoRef.current.play();
      setActive(true);
      setErr("");
    } catch (e) {
      setErr("Camera access denied. Please allow camera permission.");
    }
  }, []);

  const stop = useCallback(() => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setActive(false);
  }, []);

  const capture = useCallback(() => {
    if (!videoRef.current || !active) return null;
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext("2d").drawImage(videoRef.current, 0, 0);
    const b64 = canvas.toDataURL("image/jpeg", 0.8);
    if (onCapture) onCapture(b64);
    return b64;
  }, [active, onCapture]);

  // Expose start/stop/capture via ref
  React.useImperativeHandle(onFrame, () => ({ start, stop, capture, video: videoRef.current, canvas: canvasRef.current, active }));

  useEffect(() => { return () => stop(); }, [stop]);

  return (
    <div className="relative overflow-hidden rounded-xl bg-black">
      {err && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#1a1a1a] z-10">
          <div className="text-center text-white p-6">
            <Camera size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm opacity-70 mb-3">{err}</p>
            <button onClick={start} className="bg-[#E8956D] text-[#3A322C] px-4 py-2 rounded-lg text-sm font-bold">Enable Camera</button>
          </div>
        </div>
      )}
      <video
        ref={videoRef}
        className={`w-full ${mirrored ? "scale-x-[-1]" : ""}`}
        style={{ display: active ? "block" : "none" }}
        autoPlay muted playsInline
      />
      <canvas ref={canvasRef} className="hidden" />
      {!active && !err && (
        <div className="flex items-center justify-center bg-[#1a1a1a] h-64 cursor-pointer" onClick={start}>
          <div className="text-center text-white">
            <Camera size={40} className="mx-auto mb-3 opacity-40" />
            <p className="text-sm opacity-60">Tap to start camera</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── KIOSK TAB ───────────────────────────────────────────────────────────────
function KioskTab({ kitchens, selectedKitchen }) {
  const videoRef = useRef();
  const overlayCanvasRef = useRef();
  const loopRef = useRef(null);
  const { loaded, loading, error: modelError, load } = useFaceModels();
  const [cameraActive, setCameraActive] = useState(false);
  const [result, setResult] = useState(null); // recognition result
  const [status, setStatus] = useState("idle"); // idle | scanning | recognized | unknown | processing
  const [feedback, setFeedback] = useState(null); // {type: success|error, message}
  const [autoScan, setAutoScan] = useState(true);
  const [action, setAction] = useState("auto"); // auto | checkin | checkout

  useEffect(() => { load(); }, [load]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480, facingMode: "user" } });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setCameraActive(true);
      if (autoScan) startLoop();
    } catch { setCameraActive(false); }
  };

  const stopCamera = () => {
    stopLoop();
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setStatus("idle");
  };

  const startLoop = () => {
    if (loopRef.current) return;
    loopRef.current = setInterval(scanFrame, 2000);
  };

  const stopLoop = () => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
  };

  useEffect(() => { return () => stopLoop(); }, []);

  const scanFrame = async () => {
    if (!videoRef.current || !loaded || status === "processing") return;
    const video = videoRef.current;
    if (!video.srcObject || video.readyState < 2) return;

    setStatus("scanning");
    try {
      const detections = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (!detections) { setStatus("idle"); return; }

      // Draw box on overlay canvas
      drawBox(detections, video);

      setStatus("processing");
      const descriptor = Array.from(detections.descriptor);

      // Capture current frame image
      const imgCanvas = document.createElement("canvas");
      imgCanvas.width = video.videoWidth; imgCanvas.height = video.videoHeight;
      imgCanvas.getContext("2d").drawImage(video, 0, 0);
      const imgB64 = imgCanvas.toDataURL("image/jpeg", 0.7);

      const res = await attendanceAPI.recognize({ face_descriptor: descriptor, kitchen_id: selectedKitchen, image_base64: imgB64 });
      const data = res.data;

      if (data.matched) {
        setResult({ ...data, image_b64: imgB64 });
        setStatus("recognized");
        stopLoop(); // pause scanning until handled
      } else {
        setStatus("unknown");
        setTimeout(() => { setStatus("idle"); if (autoScan) startLoop(); }, 2500);
      }
    } catch (e) {
      setStatus("idle");
    }
  };

  const drawBox = (det, video) => {
    if (!overlayCanvasRef.current) return;
    const canvas = overlayCanvasRef.current;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#E8956D"; ctx.lineWidth = 3;
    const { x, y, width, height } = det.detection.box;
    ctx.strokeRect(x, y, width, height);
  };

  const handleAction = async (type) => {
    if (!result) return;
    setStatus("processing");
    try {
      if (type === "check_in") {
        const r = await attendanceAPI.checkIn({ staff_id: result.staff_id, kitchen_id: selectedKitchen, image_base64: result.image_b64 });
        if (r.data.already_checked_in) {
          setFeedback({ type: "warning", message: `${result.staff_name} already checked in today`, icon: "warning" });
        } else {
          setFeedback({ type: "success", message: `${result.staff_name} checked in successfully!${r.data.is_late ? " (Late)" : ""}`, icon: "success" });
        }
      } else {
        const r = await attendanceAPI.checkOut({ staff_id: result.staff_id, kitchen_id: selectedKitchen, image_base64: result.image_b64 });
        setFeedback({ type: "success", message: `${result.staff_name} checked out. Hours: ${r.data.hours_worked}h`, icon: "success" });
      }
    } catch (e) {
      setFeedback({ type: "error", message: formatError(e), icon: "error" });
    }
    setResult(null);
    setStatus("idle");
    setTimeout(() => {
      setFeedback(null);
      if (autoScan && cameraActive) startLoop();
    }, 4000);
  };

  const borderColor = { idle: "border-[#E5E1DB]", scanning: "border-[#E8956D]", recognized: "border-[#637C64]", unknown: "border-[#C2624E]", processing: "border-[#E8956D]" }[status] || "border-[#E5E1DB]";

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="text-center">
        <h2 className="text-2xl font-extrabold font-[Manrope] text-[#3A322C]">Attendance Kiosk</h2>
        <p className="text-sm text-[#8A7E73]">Face the camera to check in or out</p>
      </div>

      {(modelError) && (
        <div className="bg-[#FBEAE7] border border-[#C2624E]/30 rounded-xl px-4 py-3 text-sm text-[#C2624E]">{modelError}</div>
      )}

      {loading && (
        <div className="bg-[#FDF4EB] border border-[#D98032]/30 rounded-xl px-4 py-3 text-sm text-[#D98032] flex items-center gap-2">
          <Loader size={14} className="animate-spin" /> Loading face recognition models...
        </div>
      )}

      {/* Camera */}
      <div className={`relative rounded-2xl overflow-hidden border-4 transition-colors ${borderColor}`}>
        <video ref={videoRef} autoPlay muted playsInline
          className="w-full scale-x-[-1]"
          style={{ display: cameraActive ? "block" : "none", minHeight: 300 }} />
        <canvas ref={overlayCanvasRef}
          className="absolute inset-0 w-full h-full scale-x-[-1] pointer-events-none"
          style={{ display: cameraActive ? "block" : "none" }} />

        {!cameraActive && (
          <div className="flex items-center justify-center bg-[#1a1a1a] h-72 cursor-pointer" onClick={loaded ? startCamera : undefined}>
            <div className="text-center text-white">
              <Camera size={56} className="mx-auto mb-3 opacity-40" />
              {!loaded ? <p className="text-sm opacity-60">Loading models…</p> : <p className="text-sm opacity-60">Tap to activate camera</p>}
            </div>
          </div>
        )}

        {/* Status overlay */}
        {status === "scanning" && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#E8956D]/90 text-[#3A322C] px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
            <Loader size={14} className="animate-spin" /> Scanning...
          </div>
        )}
        {status === "unknown" && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[#C2624E]/90 text-white px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2">
            <XCircle size={14} /> Face not registered
          </div>
        )}
      </div>

      {/* Feedback banner */}
      {feedback && (
        <div className={`rounded-xl px-5 py-4 flex items-center gap-3 text-sm font-bold ${
          feedback.type === "success" ? "bg-[#EDF2EE] text-[#637C64]" :
          feedback.type === "warning" ? "bg-[#FDF4EB] text-[#D98032]" :
          "bg-[#FBEAE7] text-[#C2624E]"
        }`}>
          {feedback.type === "success" ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
          <span className="text-base">{feedback.message}</span>
        </div>
      )}

      {/* Recognized staff card */}
      {result && status === "recognized" && (
        <div className="bg-white border-2 border-[#637C64] rounded-2xl p-5 animate-fade-in-up">
          <div className="flex items-center gap-4 mb-5">
            {result.face_image ? (
              <img src={result.face_image} alt={result.staff_name} className="w-16 h-16 rounded-full object-cover border-2 border-[#637C64]" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-[#E8956D]/20 flex items-center justify-center">
                <span className="text-2xl font-bold text-[#E8956D]">{result.staff_name?.[0]}</span>
              </div>
            )}
            <div>
              <p className="text-xl font-extrabold text-[#3A322C]">{result.staff_name}</p>
              <p className="text-sm text-[#8A7E73]">{result.employee_id} · {result.designation}</p>
              <div className="flex items-center gap-1 mt-1">
                <CheckCircle size={13} className="text-[#637C64]" />
                <span className="text-xs text-[#637C64] font-semibold">Face matched ({Math.round(result.confidence * 100)}%)</span>
              </div>
            </div>
          </div>

          {/* Today status */}
          {result.today_record?.check_in && (
            <div className="bg-[#F4F3F0] rounded-xl px-4 py-2.5 mb-4 text-sm">
              <span className="text-[#8A7E73]">Today: </span>
              <span className="font-bold text-[#3A322C]">
                Checked in at {fmtTime(result.today_record.check_in)}
              </span>
              {result.today_record?.check_out && (
                <span className="text-[#8A7E73]"> → Checked out at {fmtTime(result.today_record.check_out)}</span>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            {result.today_status !== "checked_in" && result.today_status !== "checked_out" && (
              <button onClick={() => handleAction("check_in")} className="flex-1 bg-[#637C64] text-white rounded-xl py-4 font-bold text-lg flex items-center justify-center gap-2 hover:bg-[#4e6350] transition-colors">
                <LogIn size={22} /> Check In
              </button>
            )}
            {result.today_status === "checked_in" && (
              <button onClick={() => handleAction("check_out")} className="flex-1 bg-[#C2624E] text-white rounded-xl py-4 font-bold text-lg flex items-center justify-center gap-2 hover:bg-[#a85040] transition-colors col-span-2">
                <LogOut size={22} /> Check Out
              </button>
            )}
            {result.today_status === "checked_out" && (
              <div className="col-span-2 text-center text-[#637C64] font-bold py-3 bg-[#EDF2EE] rounded-xl">
                <CheckCircle size={18} className="inline mr-2" />Attendance complete for today
              </div>
            )}
            <button onClick={() => { setResult(null); setStatus("idle"); if (autoScan) startLoop(); }}
              className="flex-1 border-2 border-[#E5E1DB] rounded-xl py-4 font-bold text-[#5C5147] hover:bg-[#F4F3F0] transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex gap-3 justify-center flex-wrap">
        {!cameraActive ? (
          <button onClick={startCamera} disabled={!loaded}
            className="bg-[#E8956D] text-[#3A322C] rounded-xl px-6 py-3 font-bold flex items-center gap-2 disabled:opacity-50">
            <Camera size={18} /> Start Camera
          </button>
        ) : (
          <button onClick={stopCamera} className="border-2 border-[#E5E1DB] rounded-xl px-6 py-3 font-bold text-[#5C5147] flex items-center gap-2">
            <X size={18} /> Stop Camera
          </button>
        )}
        {cameraActive && !loopRef.current && status === "idle" && (
          <button onClick={() => { setStatus("idle"); startLoop(); }} className="bg-[#5C5147] text-white rounded-xl px-6 py-3 font-bold flex items-center gap-2">
            <RefreshCw size={18} /> Resume Scan
          </button>
        )}
        {cameraActive && (
          <button onClick={scanFrame} className="border-2 border-[#E8956D] text-[#E8956D] rounded-xl px-6 py-3 font-bold flex items-center gap-2">
            <Shield size={18} /> Scan Now
          </button>
        )}
      </div>
      <p className="text-center text-xs text-[#8A7E73]">Auto-scan runs every 2 seconds when camera is active</p>
    </div>
  );
}

// ─── STAFF TAB ───────────────────────────────────────────────────────────────
function StaffTab({ kitchens, selectedKitchen }) {
  const videoRef = useRef();
  const canvasRef = useRef();
  const scanLoopRef = useRef(null);
  const { loaded, loading: modLoading, load } = useFaceModels();
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null);
  const [faceStaff, setFaceStaff] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [faceStatus, setFaceStatus] = useState("idle");
  // idle | loading-camera | live | face-detected | capturing | saving | saved | error
  const [liveMsg, setLiveMsg] = useState("Position your face in the frame");
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedDesc, setCapturedDesc] = useState(null);
  const [faceErrMsg, setFaceErrMsg] = useState("");

  useEffect(() => { fetchAll(); }, [selectedKitchen]);

  // Load models as soon as StaffTab mounts
  useEffect(() => { load(); }, []);

  useEffect(() => {
    return () => { stopScanLoop(); stopFaceCamera(); };
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, sh] = await Promise.all([
        attendanceAPI.getStaff({ kitchen_id: selectedKitchen }),
        attendanceAPI.getShifts({ kitchen_id: selectedKitchen })
      ]);
      setStaff(s.data || []);
      setShifts(sh.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  const saveStaff = async () => {
    setError("");
    try {
      if (modal.mode === "create") await attendanceAPI.createStaff({ ...form, kitchen_id: selectedKitchen });
      else await attendanceAPI.updateStaff(modal.id, form);
      setModal(null);
      fetchAll();
    } catch (e) { setError(formatError(e)); }
  };

  const startFaceCamera = async () => {
    setFaceStatus("loading-camera");
    setFaceErrMsg("");
    setCapturedImage(null);
    setCapturedDesc(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" }
      });
      videoRef.current.srcObject = stream;
      await videoRef.current.play();
      setFaceStatus("live");
      setLiveMsg("Position your face in the frame");
      // Wait for models, then start live scan
      await load();
      startScanLoop();
    } catch (e) {
      setFaceStatus("error");
      setFaceErrMsg("Camera access denied. Please allow camera permission in your browser.");
    }
  };

  const stopFaceCamera = () => {
    stopScanLoop();
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
  };

  const stopScanLoop = () => {
    if (scanLoopRef.current) { clearInterval(scanLoopRef.current); scanLoopRef.current = null; }
  };

  const startScanLoop = () => {
    if (scanLoopRef.current) return;
    scanLoopRef.current = setInterval(async () => {
      if (!videoRef.current || !videoRef.current.srcObject) return;
      if (!faceapi.nets.tinyFaceDetector.isLoaded) return;
      const video = videoRef.current;
      if (video.readyState < 2 || video.videoWidth === 0) return;

      try {
        const det = await faceapi
          .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.3 }))
          .withFaceLandmarks(true)
          .withFaceDescriptor();

        // Draw box on canvas
        if (canvasRef.current) {
          canvasRef.current.width = video.videoWidth;
          canvasRef.current.height = video.videoHeight;
          const ctx = canvasRef.current.getContext("2d");
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          if (det) {
            ctx.strokeStyle = "#22c55e"; ctx.lineWidth = 3;
            const { x, y, width, height } = det.detection.box;
            ctx.strokeRect(x, y, width, height);
          }
        }

        if (det) {
          setFaceStatus("face-detected");
          setLiveMsg("Face detected! Click 'Capture' to register.");
        } else {
          setFaceStatus("live");
          setLiveMsg("Position your face in the frame — ensure good lighting");
        }
      } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    }, 600);
  };

  const captureFace = async () => {
    if (!videoRef.current || !videoRef.current.srcObject) return;
    setFaceStatus("capturing");
    setFaceErrMsg("");
    try {
      const video = videoRef.current;
      const det = await faceapi
        .detectSingleFace(video, new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.25 }))
        .withFaceLandmarks(true)
        .withFaceDescriptor();

      if (!det) {
        setFaceStatus("live");
        setFaceErrMsg("No face detected. Ensure your face is visible, well-lit, and centred in the frame.");
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);
      setCapturedImage(canvas.toDataURL("image/jpeg", 0.85));
      setCapturedDesc(Array.from(det.descriptor));
      setFaceStatus("detected");
      stopScanLoop();
    } catch (e) {
      setFaceStatus("live");
      setFaceErrMsg("Detection error. Please try again.");
    }
  };

  const saveFace = async () => {
    if (!capturedDesc || !faceStaff) return;
    setFaceStatus("saving");
    setFaceErrMsg("");
    try {
      await attendanceAPI.registerFace(faceStaff.id, {
        face_descriptor: capturedDesc,
        image_base64: capturedImage
      });
      setFaceStatus("saved");
      setTimeout(() => {
        stopFaceCamera();
        setModal(null);
        setFaceStatus("idle");
        setCapturedImage(null);
        setCapturedDesc(null);
        fetchAll();
      }, 1800);
    } catch (e) {
      setFaceStatus("detected");
      setFaceErrMsg("Failed to save: " + formatError(e));
    }
  };

  const retake = () => {
    setCapturedImage(null); setCapturedDesc(null);
    setFaceStatus("live"); setFaceErrMsg("");
    startScanLoop();
  };

  const openFaceReg = (s) => {
    setFaceStaff(s); setModal("register-face");
    setFaceStatus("idle"); setCapturedImage(null); setCapturedDesc(null);
    setFaceErrMsg(""); stopScanLoop();
  };

  const closeFaceReg = () => {
    stopFaceCamera();
    setModal(null);
    setFaceStatus("idle"); setCapturedImage(null); setCapturedDesc(null); setFaceErrMsg("");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setForm({ kitchen_id: selectedKitchen }); setModal({ mode: "create" }); setError(""); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5">
          <Plus size={14} /> Add Staff
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>{["Employee", "ID", "Designation", "Shift", "Face", "Actions"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {staff.map(s => (
                  <tr key={s.id} className="hover:bg-[#FAFAF8]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {s.face_image ? (
                          <img src={s.face_image} alt={s.name} className="w-9 h-9 rounded-full object-cover border border-[#E5E1DB]" />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-[#E8956D]/10 flex items-center justify-center">
                            <span className="text-sm font-bold text-[#E8956D]">{s.name?.[0]}</span>
                          </div>
                        )}
                        <span className="font-medium text-[#3A322C]">{s.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#8A7E73]">{s.employee_id}</td>
                    <td className="px-4 py-3 text-[#8A7E73]">{s.designation || "—"}</td>
                    <td className="px-4 py-3 text-[#8A7E73]">{shifts.find(sh => sh.id === s.shift_id)?.name || "—"}</td>
                    <td className="px-4 py-3">
                      {s.has_face
                        ? <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#EDF2EE] text-[#637C64] flex items-center gap-1 w-fit"><CheckCircle size={11} />Registered</span>
                        : <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[#FBEAE7] text-[#C2624E] flex items-center gap-1 w-fit"><XCircle size={11} />Not set</span>
                      }
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => openFaceReg(s)} className="text-xs bg-[#E8956D]/10 text-[#E8956D] px-2 py-1 rounded font-bold hover:bg-[#E8956D]/20 flex items-center gap-1">
                          <Camera size={11} /> {s.has_face ? "Update" : "Register"} Face
                        </button>
                        <button onClick={() => { setForm({ ...s }); setModal({ mode: "edit", id: s.id }); setError(""); }} className="text-[#8A7E73] hover:text-[#E8956D] p-1"><Edit size={14} /></button>
                        <button onClick={async () => { if (!await confirm("Deactivate?")) return; await attendanceAPI.deleteStaff(s.id); fetchAll(); }} className="text-[#8A7E73] hover:text-[#C2624E] p-1"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {staff.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No staff registered yet</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {modal && modal !== "register-face" && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-lg text-[#3A322C]">{modal.mode === "create" ? "Add Staff" : "Edit Staff"}</h2>
              <button onClick={() => setModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Full Name *</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Employee ID *</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.employee_id || ""} onChange={e => setForm({ ...form, employee_id: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Designation</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. Cook, Helper, Supervisor..." value={form.designation || ""} onChange={e => setForm({ ...form, designation: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Phone</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.phone || ""} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Shift</label>
                <select className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.shift_id || ""} onChange={e => setForm({ ...form, shift_id: e.target.value || null })}>
                  <option value="">No Shift</option>
                  {shifts.map(sh => <option key={sh.id} value={sh.id}>{sh.name} ({sh.start_time} - {sh.end_time})</option>)}
                </select></div>
              {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={saveStaff} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Face Registration Modal */}
      {modal === "register-face" && faceStaff && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <div>
                <h2 className="font-bold text-lg text-[#3A322C]">Register Face</h2>
                <p className="text-xs text-[#8A7E73]">{faceStaff.name} — {faceStaff.employee_id}</p>
              </div>
              <button onClick={closeFaceReg} className="text-[#8A7E73] hover:text-[#3A322C]"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">

              {/* Model loading */}
              {modLoading && (
                <div className="bg-[#FDF4EB] rounded-lg px-4 py-2.5 flex items-center gap-2 text-sm text-[#D98032]">
                  <Loader size={14} className="animate-spin flex-shrink-0" />
                  Loading face recognition models… (first time only)
                </div>
              )}

              {/* Step 1: Idle — start camera */}
              {faceStatus === "idle" && (
                <div
                  className="h-52 flex flex-col items-center justify-center bg-[#1a1a1a] rounded-xl cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={startFaceCamera}
                >
                  <Camera size={36} className="text-white/40 mb-3" />
                  <p className="text-white/60 text-sm font-medium">Tap to start camera</p>
                  <p className="text-white/30 text-xs mt-1">Camera permission required</p>
                </div>
              )}

              {/* Camera loading */}
              {faceStatus === "loading-camera" && (
                <div className="h-52 flex items-center justify-center bg-[#1a1a1a] rounded-xl">
                  <div className="text-center text-white">
                    <Loader size={28} className="animate-spin mx-auto mb-3 opacity-60" />
                    <p className="text-sm opacity-60">Starting camera…</p>
                  </div>
                </div>
              )}

              {/* Error */}
              {faceStatus === "error" && (
                <div className="bg-[#FBEAE7] rounded-xl p-4 text-center">
                  <XCircle size={32} className="text-[#C2624E] mx-auto mb-2" />
                  <p className="text-sm text-[#C2624E] font-medium">{faceErrMsg || "Camera error"}</p>
                  <button onClick={() => setFaceStatus("idle")} className="mt-3 text-xs text-[#E8956D] font-bold underline">Try Again</button>
                </div>
              )}

              {/* Live camera + canvas overlay */}
              {(faceStatus === "live" || faceStatus === "face-detected" || faceStatus === "capturing") && (
                <div>
                  <div className={`relative rounded-xl overflow-hidden border-2 transition-colors ${faceStatus === "face-detected" ? "border-green-500" : "border-[#E5E1DB]"}`}>
                    <video
                      ref={videoRef} autoPlay muted playsInline
                      className="w-full block"
                      style={{ transform: "scaleX(-1)", maxHeight: 260, background: "#000", objectFit: "cover" }}
                    />
                    <canvas
                      ref={canvasRef}
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{ transform: "scaleX(-1)" }}
                    />
                    {/* Live status chip */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2">
                      {faceStatus === "face-detected" && (
                        <span className="bg-green-500/90 text-white text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                          <CheckCircle size={12} /> Face Detected — Ready to Capture
                        </span>
                      )}
                      {faceStatus === "live" && (
                        <span className="bg-black/60 text-white/70 text-xs px-3 py-1.5 rounded-full backdrop-blur-sm">
                          {liveMsg}
                        </span>
                      )}
                      {faceStatus === "capturing" && (
                        <span className="bg-[#E8956D]/90 text-[#3A322C] text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                          <Loader size={11} className="animate-spin" /> Detecting face…
                        </span>
                      )}
                    </div>
                  </div>

                  {faceErrMsg && (
                    <div className="mt-2 bg-[#FBEAE7] rounded-lg px-3 py-2 text-xs text-[#C2624E] font-medium">{faceErrMsg}</div>
                  )}

                  <button
                    onClick={captureFace}
                    disabled={faceStatus === "capturing"}
                    className={`w-full mt-3 rounded-xl py-3 font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                      faceStatus === "face-detected"
                        ? "bg-[#637C64] text-white hover:bg-[#4e6350]"
                        : "bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661]"
                    } disabled:opacity-60`}
                  >
                    {faceStatus === "capturing"
                      ? <><Loader size={15} className="animate-spin" /> Detecting…</>
                      : <><Camera size={15} /> {faceStatus === "face-detected" ? "Capture This Face" : "Capture Face"}</>
                    }
                  </button>
                </div>
              )}

              {/* Captured preview */}
              {(faceStatus === "detected" || faceStatus === "saving" || faceStatus === "saved") && capturedImage && (
                <div className="space-y-4">
                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <img
                        src={capturedImage} alt="captured"
                        className="w-36 h-36 object-cover rounded-full border-4 border-[#637C64]"
                        style={{ transform: "scaleX(-1)" }}
                      />
                      <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-[#637C64] rounded-full flex items-center justify-center">
                        <CheckCircle size={18} className="text-white" />
                      </div>
                    </div>
                  </div>

                  {faceStatus === "saved" ? (
                    <div className="text-center">
                      <p className="text-[#637C64] font-bold text-lg flex items-center justify-center gap-2">
                        <CheckCircle size={20} /> Face Registered Successfully!
                      </p>
                      <p className="text-xs text-[#8A7E73] mt-1">This staff member can now use the kiosk</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-center text-sm text-[#637C64] font-medium">
                        Face captured. {faceStaff.name?.split(" ")[0]} looks good!
                      </p>
                      {faceErrMsg && (
                        <div className="bg-[#FBEAE7] rounded-lg px-3 py-2 text-xs text-[#C2624E] font-medium">{faceErrMsg}</div>
                      )}
                      <div className="flex gap-3">
                        <button onClick={retake} disabled={faceStatus === "saving"}
                          className="flex-1 border border-[#E5E1DB] rounded-xl py-2.5 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] disabled:opacity-50">
                          Retake
                        </button>
                        <button onClick={saveFace} disabled={faceStatus === "saving"}
                          className="flex-1 bg-[#637C64] text-white rounded-xl py-2.5 text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#4e6350] disabled:opacity-60">
                          {faceStatus === "saving"
                            ? <><Loader size={14} className="animate-spin" /> Saving…</>
                            : <><CheckCircle size={14} /> Save & Register</>
                          }
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Guide tips */}
              {(faceStatus === "live" || faceStatus === "face-detected" || faceStatus === "idle") && (
                <div className="grid grid-cols-3 gap-2 text-center">
                  {[
                    { emoji: "💡", tip: "Good lighting" },
                    { emoji: "👤", tip: "Face forward" },
                    { emoji: "📏", tip: "Arm's length" },
                  ].map(t => (
                    <div key={t.tip} className="bg-[#F4F3F0] rounded-lg py-2 px-1">
                      <p className="text-base">{t.emoji}</p>
                      <p className="text-[10px] text-[#8A7E73] font-medium mt-0.5">{t.tip}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── RECORDS TAB ─────────────────────────────────────────────────────────────
function RecordsTab({ kitchens, selectedKitchen }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [todaySummary, setTodaySummary] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        attendanceAPI.getRecords({ kitchen_id: selectedKitchen, date_str: date, limit: 200 }),
        attendanceAPI.getTodaySummary(selectedKitchen)
      ]);
      setRecords(r.data || []);
      setTodaySummary(s.data);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  }, [selectedKitchen, date]);

  useEffect(() => { if (selectedKitchen) fetch(); }, [fetch]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end flex-wrap">
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Date</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={date} onChange={e => setDate(e.target.value)} /></div>
        <button onClick={fetch} className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm font-bold text-[#5C5147] hover:bg-[#F4F3F0] flex items-center gap-1"><RefreshCw size={14} /> Refresh</button>
      </div>

      {todaySummary && date === new Date().toISOString().split("T")[0] && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "Total Staff", value: todaySummary.total_staff, color: "bg-blue-50 text-blue-700" },
            { label: "Present", value: todaySummary.present, color: "bg-[#EDF2EE] text-[#637C64]" },
            { label: "Absent", value: todaySummary.absent, color: "bg-[#FBEAE7] text-[#C2624E]" },
            { label: "Late", value: todaySummary.late_count, color: "bg-[#FDF4EB] text-[#D98032]" },
          ].map(s => (
            <div key={s.label} className={`${s.color} rounded-xl p-4 text-center`}>
              <p className="text-2xl font-extrabold font-[Manrope]">{s.value}</p>
              <p className="text-xs font-bold mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>{["Staff", "Employee ID", "Check In", "Check Out", "Hours", "Late"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {records.map(r => (
                  <tr key={r.id} className={`hover:bg-[#FAFAF8] ${r.is_late ? "bg-[#FDF4EB]/30" : ""}`}>
                    <td className="px-4 py-3 font-medium text-[#3A322C]">{r.staff_name}</td>
                    <td className="px-4 py-3 text-[#8A7E73]">{r.employee_id}</td>
                    <td className="px-4 py-3 text-[#637C64] font-medium">{r.check_in ? fmtTime(r.check_in) : "—"}</td>
                    <td className="px-4 py-3 text-[#C2624E] font-medium">{r.check_out ? fmtTime(r.check_out) : "—"}</td>
                    <td className="px-4 py-3 font-bold">{r.hours_worked ? `${r.hours_worked}h` : "—"}</td>
                    <td className="px-4 py-3">{r.is_late ? <span className="text-xs bg-[#FDF4EB] text-[#D98032] px-2 py-0.5 rounded-full font-bold">Late</span> : <span className="text-xs text-[#C4BAAF]">—</span>}</td>
                  </tr>
                ))}
                {records.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No records for this date</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── SHIFTS TAB ──────────────────────────────────────────────────────────────
function ShiftsTab({ kitchens, selectedKitchen }) {
  const [shifts, setShifts] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [error, setError] = useState("");

  useEffect(() => { fetchShifts(); }, [selectedKitchen]);
  const fetchShifts = async () => {
    const r = await attendanceAPI.getShifts(selectedKitchen ? { kitchen_id: selectedKitchen } : {});
    setShifts(r.data || []);
  };

  const save = async () => {
    setError("");
    try {
      if (modal.mode === "create") await attendanceAPI.createShift({ ...form, kitchen_id: selectedKitchen || null });
      else await attendanceAPI.updateShift(modal.id, { ...form, kitchen_id: selectedKitchen || null });
      setModal(null); fetchShifts();
    } catch (e) { setError(formatError(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => { setForm({ late_threshold_minutes: 15 }); setModal({ mode: "create" }); setError(""); }}
          className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1.5">
          <Plus size={14} /> Add Shift
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {shifts.map(s => (
          <div key={s.id} className="bg-white border border-[#E5E1DB] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-[#3A322C]">{s.name}</h3>
              <div className="flex gap-1">
                <button onClick={() => { setForm({ ...s }); setModal({ mode: "edit", id: s.id }); setError(""); }} className="p-1 text-[#8A7E73] hover:text-[#E8956D]"><Edit size={14} /></button>
                <button onClick={async () => { await attendanceAPI.deleteShift(s.id); fetchShifts(); }} className="p-1 text-[#8A7E73] hover:text-[#C2624E]"><Trash2 size={14} /></button>
              </div>
            </div>
            <p className="text-2xl font-extrabold text-[#E8956D]">{s.start_time} <span className="text-[#8A7E73] text-base font-normal">→</span> {s.end_time}</p>
            <p className="text-xs text-[#8A7E73] mt-1">Late after {s.late_threshold_minutes} min</p>
          </div>
        ))}
        {shifts.length === 0 && <div className="col-span-3 text-center py-12 text-[#8A7E73]"><Clock size={40} className="mx-auto mb-3 opacity-20" /><p>No shifts created yet</p></div>}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm animate-fade-in-up">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E1DB]">
              <h2 className="font-bold text-[#3A322C]">{modal.mode === "create" ? "Add Shift" : "Edit Shift"}</h2>
              <button onClick={() => setModal(null)}><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Shift Name *</label>
                <input className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" placeholder="e.g. Morning, Night..." value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Start Time</label>
                  <input type="time" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.start_time || ""} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
                <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">End Time</label>
                  <input type="time" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.end_time || ""} onChange={e => setForm({ ...form, end_time: e.target.value })} /></div>
              </div>
              <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">Late Threshold (minutes)</label>
                <input type="number" min="0" max="120" className="w-full border border-[#E5E1DB] rounded-lg px-3 py-2.5 text-sm outline-none focus:border-[#E8956D]" value={form.late_threshold_minutes ?? 15} onChange={e => setForm({ ...form, late_threshold_minutes: parseInt(e.target.value) || 0 })} /></div>
              {error && <p className="text-sm text-[#C2624E] bg-[#FBEAE7] px-3 py-2 rounded-lg">{error}</p>}
              <div className="flex gap-3">
                <button onClick={() => setModal(null)} className="flex-1 border border-[#E5E1DB] rounded-lg py-2.5 text-sm font-bold text-[#5C5147]">Cancel</button>
                <button onClick={save} className="flex-1 bg-[#E8956D] rounded-lg py-2.5 text-sm font-bold text-[#3A322C]">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── REPORTS TAB ─────────────────────────────────────────────────────────────
function ReportsTab({ kitchens, selectedKitchen }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; });
  const [dateTo, setDateTo] = useState(new Date().toISOString().split("T")[0]);

  const fetch = async () => {
    setLoading(true);
    try {
      const r = await attendanceAPI.getReport({ kitchen_id: selectedKitchen, date_from: dateFrom, date_to: dateTo });
      setData(r.data || []);
    } catch (e) { if (process.env.NODE_ENV === 'development') console.error('Fetch error:', e); }
    setLoading(false);
  };

  useEffect(() => { if (selectedKitchen) fetch(); }, [selectedKitchen]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end flex-wrap">
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">From</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={dateFrom} onChange={e => setDateFrom(e.target.value)} /></div>
        <div><label className="text-xs font-bold uppercase text-[#8A7E73] mb-1 block">To</label>
          <input type="date" className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]" value={dateTo} onChange={e => setDateTo(e.target.value)} /></div>
        <button onClick={fetch} className="bg-[#E8956D] text-[#3A322C] rounded-lg px-4 py-2 text-sm font-bold">Generate Report</button>
      </div>
      {loading ? <div className="flex justify-center py-12"><Loader className="animate-spin text-[#E8956D]" /></div> : (
        <div className="bg-white border border-[#E5E1DB] rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F4F3F0]">
                <tr>{["Staff", "ID", "Days Present", "Total Hours", "Avg Hours/Day", "Late Days"].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-[#5C5147]">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-[#F4F3F0]">
                {data.map(r => (
                  <tr key={r.staff_id} className="hover:bg-[#FAFAF8]">
                    <td className="px-4 py-3 font-medium text-[#3A322C]">{r.staff_name}</td>
                    <td className="px-4 py-3 text-[#8A7E73]">{r.employee_id}</td>
                    <td className="px-4 py-3 font-bold text-[#637C64]">{r.days_present}</td>
                    <td className="px-4 py-3 font-bold">{r.total_hours}h</td>
                    <td className="px-4 py-3 text-[#5C5147]">{r.avg_hours}h</td>
                    <td className="px-4 py-3">{r.late_count > 0 ? <span className="text-xs bg-[#FDF4EB] text-[#D98032] px-2 py-0.5 rounded-full font-bold">{r.late_count}</span> : <span className="text-[#C4BAAF]">0</span>}</td>
                  </tr>
                ))}
                {data.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8A7E73]">No data for selected period</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const TABS = [
  { id: "kiosk", label: "Kiosk", icon: Camera },
  { id: "staff", label: "Staff", icon: Users },
  { id: "records", label: "Records", icon: UserCheck },
  { id: "shifts", label: "Shifts", icon: Clock },
  { id: "reports", label: "Reports", icon: BarChart3 },
];

export default function Attendance() {
  const [activeTab, setActiveTab] = useState("kiosk");
  const [kitchens, setKitchens] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState("");

  useEffect(() => {
    usersAPI.getKitchens().then(r => {
      const ks = r.data || [];
      setKitchens(ks);
      if (ks.length > 0) setSelectedKitchen(ks[0].id);
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-6 max-w-5xl" data-testid="attendance-module">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold font-[Manrope] text-[#3A322C]">Attendance</h1>
          <p className="text-sm text-[#8A7E73] mt-0.5">Facial recognition attendance for kitchen staff</p>
        </div>
        <div className="flex items-center gap-3">
          {activeTab === "kiosk" && (
            <a
              href="/kiosk"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#E8956D] text-[#3A322C] hover:bg-[#D48661] transition-colors rounded-lg px-4 py-2.5 font-bold text-sm flex items-center gap-2 shadow-sm"
              data-testid="open-kiosk-btn"
            >
              <Camera size={16} /> Open Full Kiosk Mode
            </a>
          )}
          {activeTab !== "kiosk" && (
          <select
            className="border border-[#E5E1DB] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#E8956D]"
            value={selectedKitchen} onChange={e => setSelectedKitchen(e.target.value)}
          >
            {kitchens.map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
          </select>
          )}
        </div>
      </div>

      <div className="flex border-b border-[#E5E1DB] overflow-x-auto">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-5 py-3 text-sm font-semibold flex items-center gap-2 whitespace-nowrap transition-colors -mb-px ${activeTab === t.id ? "border-b-2 border-[#E8956D] text-[#E8956D]" : "text-[#8A7E73] hover:text-[#5C5147]"}`}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {selectedKitchen && (
        <>
          {activeTab === "kiosk" && <KioskTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
          {activeTab === "staff" && <StaffTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
          {activeTab === "records" && <RecordsTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
          {activeTab === "shifts" && <ShiftsTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
          {activeTab === "reports" && <ReportsTab kitchens={kitchens} selectedKitchen={selectedKitchen} />}
        </>
      )}
    </div>
  );
}
